import {
  Design,
  LokiItem,
  LokiObjectType,
  LokiValue,
  Subject
} from '../types';
import { defaultLokiValue } from './const';
import {
  DatabaseProps,
  DatabaseService,
  IDatabaseService
} from './database-service';
import { IGlobalModalsService } from './global-modals-service';

type LokiStores = {
  LokiAKV: LokiItem;
};

const databaseProps: DatabaseProps<LokiStores> = {
  name: 'LokiCatalog',
  stores: {
    LokiAKV: {
      key: 'key',
      indexes: {
        app: {
          key: 'app'
        },
        appkey: {
          key: 'appkey'
        },
        key: {
          key: 'key'
        }
      }
    }
  },
  version: 2
};

/**
 * @deprecated Kept up to date for backwards compatibility when we looked in
 * @see localStorage to see which designs were in the @see IDBFactory API.
 */
const storesIndexKey = 'storesIndex';

type StoreSerializedConfig = {
  /** Whether user should be warned when they already have a version of the branch @default true. */
  confirmConflict?: boolean;
};

/**
 * Used to access @see Design data stored in the @see IDBFactory API.
 *
 * @note
 * This is named after [lokijs](https://www.npmjs.com/package/lokijs) the
 * library that was handling the APIs for us before we created @see DatabaseService.
 */
export class LokiService {
  readonly serializedDesignsChange = new Subject<Design[]>();
  private _databaseService: IDatabaseService<LokiStores>;
  private _globalModalsService: IGlobalModalsService;
  private _serializedDesigns: Design[] = [];

  /** @deprecated Old list of serialized IDs stored. */
  private get _legacyStoredSerialized(): string[] {
    return JSON.parse(localStorage.getItem(storesIndexKey) ?? '[]');
  }
  private set _legacyStoredSerialized(ids: string[]) {
    localStorage.setItem(storesIndexKey, JSON.stringify(ids));
  }

  constructor(
    databaseService: IDatabaseService<LokiStores>,
    globalModalsService: IGlobalModalsService
  ) {
    this._databaseService = databaseService;
    this._globalModalsService = globalModalsService;
  }

  /** Create an instance of @see LokiService. */
  static create(
    globalModalsService: IGlobalModalsService
  ): LokiService {
    return new LokiService(
      new DatabaseService(databaseProps),
      globalModalsService
    );
  }

  /** Get a locally stored serialized design. */
  async getSerialized(branchId: string): Promise<Design | null> {
    const lokiItem = await this._databaseService.getByIndex(
      'LokiAKV',
      'app',
      branchId
    );
    const serializedDesigns = (!lokiItem) ? [] : this._parseSerializedDesigns(lokiItem);
    return (serializedDesigns.length !== 1) ? null : serializedDesigns[0];
  }

  /** Get all locally stored serialized designs. */
  async getSerializedDesigns(): Promise<Design[]> {
    const lokiItems = await this._databaseService.getAll('LokiAKV');
    return lokiItems.flatMap((item) => {
      return this._parseSerializedDesigns(item);
    });
  }

  /**
   * Start listening to the DB changes.
   *
   * @note This must be called in main.tsx until we mock this service for tests.
   */
  init(): void {
    let request = 0;
    const fetchSerializedDesigns = async () => {
      const thisRequest = ++request;
      const serializedDesigns = await this.getSerializedDesigns();
      if (thisRequest === request) {
        this._serializedDesigns = serializedDesigns;
        this.serializedDesignsChange.next(serializedDesigns);
      }
    };
    fetchSerializedDesigns();
    this._databaseService.databaseChange.subscribe(fetchSerializedDesigns);
  }

  async removeSerialized(branchId: string): Promise<void> {
    await this._databaseService.removeByIndex(
      'LokiAKV',
      'app',
      branchId
    );
    const legacyIds = this._legacyStoredSerialized;
    if (!legacyIds.includes(branchId)) {
      this._legacyStoredSerialized = legacyIds.filter((id) => {
        return id !== branchId;
      });
    }
  }

  /** Locally store a serialized design. */
  async storeSerialized(
    serialized: Design,
    { confirmConflict = true }: StoreSerializedConfig = {}
  ): Promise<Design | null> {
    if (
      confirmConflict
      && this._serializedDesigns.some(({ branchId }) => {
        return branchId === serialized.branchId;
      })
      && !await this._globalModalsService.openConfirmationModal({
        children: window.l.notification.override_local_branch
      })
    ) {
      return null;
    }

    const existingItem = await this._databaseService.getByIndex('LokiAKV', 'app', serialized.branchId);
    const newItem = this._createLokiItem(serialized);
    if (existingItem) {
      // If undefined, an incremental ID will be assigned.
      newItem.id = existingItem.id;
    }
    await this._databaseService.update('LokiAKV', newItem);
    const legacyIds = this._legacyStoredSerialized;
    if (!legacyIds.includes(serialized.branchId)) {
      this._legacyStoredSerialized = [
        ...legacyIds,
        serialized.branchId
      ];
    }

    return serialized;
  }

  /** Create a collection with the serialized object in it to store in the local database. */
  private _createLokiItem(serialized: Design): LokiItem {
    const { branchId } = serialized;
    const defaultCollection = defaultLokiValue.collections[0];
    const value: LokiValue = {
      ...defaultLokiValue,
      collections: [
        {
          ...defaultCollection,
          data: [{
            $loki: 1,
            meta: {
              created: Date.now(),
              revision: 0,
              version: 0
            },
            ...serialized
          }]
        }
      ]
    };
    return {
      app: branchId,
      // Yes, Loki had the ID repeated for some reason.
      appkey: `${branchId},${branchId}`,
      key: branchId,
      val: JSON.stringify(value)
    };
  }

  /** Parse the Loki item into the values like @see Design that were stored in its collections. */
  private _parseSerializedDesigns({ val }: LokiItem): Design[] {
    const item = JSON.parse(val) as LokiValue;
    return item.collections.filter((collection) => {
      return collection.objType === LokiObjectType.serializedDesigns;
    }).flatMap((collection) => {
      return collection.data;
    });
  }
}
